<template>
  <section class="dataset-view">
    <div id="savedSearchPopup">
      <saved-search-popup
          :is-adding="isSavedSearchAdding"
          :is-visible="isSavedSearchVisible"
          :active-tab="activeSavedSearchTab"
          :dial-back-hours="DIAL_BACK_HOURS"
          :favorites="favorites"
          :history="history"
          @popup-close="popupClose"
          @favorite-added="addNewFavorite"
          @delete-favorite="deleteFavorite"
          @delete-history="deleteHistory"
          @enable-favorite="enableFavorite"
          @enable-history="enableHistory"
          @favorite-renamed="favoriteRenamed"
      />
    </div>
    <section class="dataset-header">
        <div class="vy-row">
            <div class="col-md-4 col-xs-12">
                <h1 class="capture-view-title">{{ title }}</h1>
            </div>

            <div class="col-md-5 col-xs-12">
                <dial-back-component
                    v-if="hasDateRangePicker"
                    :date-range-model="dateRangeModel"
                    @dial-back-current-range="dialBackCurrentRangeHandler"
                />
                <date-selection-popup
                    v-if="hasExtendedDateRangePicker"
                    :date-text="currentDateText"
                    @range-selected="dialBackCurrentRangeHandler"
                />
            </div>

            <div class="col-md-3 col-xs-12">
              <div class="buttonContainer pull-right">
                <button
                  class="btn assurance-header-button left-most assurance-capture-button vyopta-btn-light-blue page-capture"
                  @click="showCaptureModal"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  title="Capture page"
                >
                    <i class="far fa-camera page-camera"></i>
                </button>
                <v-tooltip bottom v-if="hasCsvActions">
                  <template v-slot:activator="{on}">
                    <button
                      v-on="on"
                      class="btn assurance-header-button upload-download-icon"
                      @click="showUploadDownloadModal = true"
                      text
                    >
                      <i class="far fa-file-spreadsheet"></i>
                    </button>
                  </template>
                  <span>CSV Actions</span>
                </v-tooltip>
                <button
                  class="btn assurance-header-button"
                  @click="openHistory"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  title="View recent search history"
                >
                    <i class="far fa-history"></i>
                </button>
                <button class="btn assurance-header-button"
                  @click="openFavorites"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  title="View saved searches"
                >
                    <i class="far fa-book-heart"></i>
                </button>
                <button
                  :class="{ disabled: !filterModel.getActiveFilters() }"
                  class="btn assurance-header-button right-most"
                  @click="addFavorite"
                  data-toggle="tooltip"
                  data-placement="bottom" title="Save search">
                    <i class="far fa-bookmark"></i>
                </button>
              </div>
            </div>
        </div>
    </section>

    <section class="filter-expression-wrapper" v-if="hasDashboard">
        <filter-expression
            v-if="filterColumns.length > 0"
            class="header-filter-expression"
            :columns="filterColumns"
            :dataset-name="datasetName"
            :filter-groups="filterModel.getActiveFilters().filterGroups"
            mode="simple"
            :operator="filterModel.getActiveFilters().operator"
            @filters-updated="updateFilterGroups"
            @operator-changed="updateOperator"
        />
    </section>

    <section v-if="hasDashboard" class="dashboardSection">
      <DashboardContainer
          :app-id="appId"
          :view-id="viewId"
          :dataset-name="datasetName"
          :date-range="dateRangeModel.getCurrentRange()"
          :is-live-mode="isLiveMode"
          :filter-model="filterModel"
          :filter-ready-signal="filterReadySignal"
          :nested-field-separator="nestedFieldSeparator"
          :panels="datasetMetadata.panels"
          :column-metadata="datasetMetadata.columns"
          @filters-updated="updateFilterGroups"
      ></DashboardContainer>
    </section>

    <section id="datasetView" class="dataset-view-content styleScroll" :class="{ 'drawer-open': drawerOpen}">
      <data-table
          :key="datasetName"
          :app-id="appId"
          :view-id="viewId"
          :dataset-name="datasetName"
          :is-live-mode="isLiveMode"
          :disable-row-selection="true"
          :date-range="dateRangeModel.getCurrentRange()"
          :filter-model="filterModel"
          :filter-ready-signal="filterReadySignal"
          :hide-filter="hasDashboard"
          :nested-field-separator="nestedFieldSeparator"
          :auto-reload-callback="autoReloadCallback"
          :data-loaded-callback="dataLoadedCallback"
          :row-click-callback="rowClickCallback"
          :watch-org-context="watchOrgContext"
          @edit-metadata="editRow"
      ></data-table>
    </section>
    <div id="bottomDrawerDataset" v-if="bottomDrawerDatasetName">
      <div class="dataset-view-bottom-drawer"
           :class="{ 'show-drawer': drawerOpen, 'drawer-expanded': drawerExpanded}"
      >
        <div class="bottom-drawer-header">
          <div class="bottom-drawer-border"></div>
          <span class="bottom-drawer-title" v-dompurify-html="bottomDrawerTitle"></span>
          <div class="bottom-drawer-icons">
            <i v-if="!drawerExpanded" class="fal fa-square-chevron-up" @click="drawerExpanded =  !drawerExpanded" title="Expand Participants"></i>
            <i v-if="drawerExpanded" class="fal fa-square-chevron-down" @click="drawerExpanded =  !drawerExpanded" title="Collapse Participants"></i>
            <i class="fal fa-times-circle" @click="closeBottomDrawer" title="Close Participants"></i>
          </div>
        </div>
        <div class="bottom-drawer-table-container">
          <data-table
              id="bottom-drawer-table"
              :key="bottomDrawerDatasetName"
              :app-id="appId"
              :view-id="viewId"
              :dataset-name="bottomDrawerDatasetName"
              :date-range="dateRangeModel.getCurrentRange()"
              :disable-row-selection="true"
              :hide-edit="hideBottomDrawerEdit"
              :hide-filter="true"
              :local-data-mode="true"
              :local-table-data="bottomDrawerTableData"
              :nested-field-separator="nestedFieldSeparator"
          ></data-table>
        </div>
      </div>
    </div>


    <EditModal
      v-if="datasetName"
      :columns="editColumns"
      :editingRow="editingRow"
      :objectName="title.slice(0, -1)"
      :primaryKey="getPrimaryKey"
      :saveService="getActiveService"
      :showEditModal="showRowEditModal"
      @finished="editFinished"
    />
    <CsvActionsModal
      :show-upload-download-modal="showUploadDownloadModal"
      :dataset-name="datasetName"
      @close="showUploadDownloadModal = false"
    ></CsvActionsModal>
  </section>
</template>
<script>
import _ from 'lodash';
import ko from 'knockout';
import Signals from 'signals';
import EditModal from '../../editModal/src/editModal.vue';
import CsvActionsModal from '../../../modules/admin/components/BuildingsSpaces/CsvActionsModal.vue';
import BuildingsService from '../../../modules/admin/services/BuildingsService';
import NetworksService from '../../../modules/admin/services/NetworksService';
import SpacesService from '../../../modules/admin/services/SpacesService';
import SensorsService from '../../../modules/admin/services/SensorsService';
import DataProvider from "../../../app/data-framework/data-provider";
import DataServiceSecure from '../../../modules/assurance/services/data-secure.service';
import DataTable from '../../dataTable/src/dataTable.vue';
import DashboardContainer from '../../dashboardContainer/src/dashboardContainer.vue';
import DateSelectionPopup from '../../dateSelectionPopup/dateSelectionPopup.vue';
import DialBackComponent from '../../dialBackComponent/dialBackComponent.vue';
import DateRangeModel from '../../../components/dialBackInput/date-range-picker.model';
import FilterExpression from '../../filterExpression/src/filterExpression.vue';
import FilterModel from '../../../modules/assurance/components/filter/data/filter.model';
import GridModel from '../../../modules/assurance/components/grid/data/grid.model';
import FilterExpressionService from '../../../modules/assurance/services/filter.expression.service';
import MetadataService from '../../../services/metadata.service';
import PreferenceService from "../../../services/preference.service";
import SavedSearchPopup from '../../savedSearchPopup/savedSearchPopup.vue';
import LoginService from '../../../services/login.service';
import DocumentService from "../../../modules/admin/services/DocumentService";
import VueDatasetService from "../../../services/vue-dataset-view.service";
import WebResourceSecure from "../../../modules/assurance/services/web-resource-secure.service";

const FAVORITES_TAB = 'favorites';
const HISTORY_TAB = 'history';
const REFRESH_RATE_MS = 30 * 1000;
const BOTTOM_DRAWER_DATASET_MAP = {
  'meetings': 'participants'
}
const DIAL_BACK_TO_FAVORITE_LIMITS = {
  4: 1,
  24: 5,
  168: 10,
  720: 1000,
};
const DIAL_BACK_TO_HISTORY_LIMITS = {
  4: 10,
  24: 10,
  168: 1000,
  720: 1000,
};

const CSV_MAP = { // todo make this metadata driven
  buildings: BuildingsService,
  networks: NetworksService,
  spaces: SpacesService,
  sensors: SensorsService,
}

const SERVICE_MAP = {
  buildings: BuildingsService,
  networks: NetworksService,
  spaces: SpacesService,
  sensors: SensorsService,
  'last-documents': DocumentService,
  'all-documents': DocumentService,
  'documents': DocumentService,
}

const DATASET_TO_PRIMARY_KEY = {
  buildings: 'id',
  networks: 'network.id',
  spaces: 'van_room.id',
  sensors: 'id',
  'last-documents': 'adr_LastDoc.DocId',
  'all-documents': 'id',
  'documents': 'id',
}

export default {
  name: 'DatasetView',
  props: {
    hideTableFilter: {
      type: Boolean,
      default: false
    },
    // from knockout params
    routeParams: { type: Object, required: true }
  },
  data: function() {
    let self = this;
    let appId = this.routeParams.appId;
    let datasetName = this.routeParams.datasetName;
    let drm = new DateRangeModel(appId);
    if (this.routeParams.liveModeStatus !== undefined) {
      drm.setLiveModeStatus(this.routeParams.liveModeStatus);
    }
    
    let pageId = this.routeParams.pageId || 'dataset';
    let filterModel = new FilterModel(true, appId + pageId);
    filterModel.activeFiltersUpdated.add(
        function () {
          self.updateHistory();
        }
    );

    return {
      activeSavedSearchTab: HISTORY_TAB,
      appId: appId,
      datasetMetadata: {},
      editingRow: {},
      error: '',
      filterColumns: [],
      hasDateRangePicker: this.routeParams.hasDateRangePicker,
      hasExtendedDateRangePicker: this.routeParams.hasExtendedDateRangePicker,
      hasDashboard: this.routeParams.hasDashboard,
      pageId: pageId,
      title: this.routeParams.title || 'Dataset',
      viewId: 'dataset_' + datasetName.replace('.', '_'),
      bottomDrawerTableDataLoading: false,
      bottomDrawerTableData: [],
      bottomDrawerTitle: '',
      gridModel: new GridModel(),
      selectedRow: undefined,
      DIAL_BACK_HOURS: LoginService.userObject.cpmmDialBackHours,
      datasetName: datasetName,
      drawerExpanded: false,
      drawerOpen: false,
      nestedFieldSeparator: this.routeParams.nestedFieldSeparator,
      bottomDrawerDatasetName: BOTTOM_DRAWER_DATASET_MAP[datasetName],
      favorites: [],
      history: [],
      hideBottomDrawerEdit: !DataProvider.getInstance().isInternalUser(),
      isSavedSearchAdding: false,
      isSavedSearchVisible: false,
      dateRangeModel: drm,
      filterReadySignal: new Signals.Signal(),
      filterModel: filterModel,
      showRowEditModal: false,
      showUploadDownloadModal: false,
      reloadData: undefined,
      watchOrgContext: this.routeParams.watchOrgContext,
    };
  },
  computed: {
    currentDateText: function() {
      let range = this.dateRangeModel.getCurrentRange();
      let formattedStart = moment(range.startDate).format(DateRangeModel.defaultFormat);
      let formattedEnd = moment(range.endDate).format(DateRangeModel.defaultFormat);
      return `${formattedStart} - ${formattedEnd}`;
    },
    dateRange: function() {
      return this.hasDateRangePicker ? this.dateRangeModel.getCurrentRange() : undefined;
    },
    editColumns: function() {
      let columns = this.datasetMetadata.columns;
      if (columns) {
        return  columns.filter((column) => column.editable === true);
      }
      return [];
    },
    getActiveService: function() {
      return SERVICE_MAP[this.datasetName];
    },
    getPrimaryKey: function() {
      return DATASET_TO_PRIMARY_KEY[this.datasetName];
    },
    hasCsvActions: function() {
      return !!CSV_MAP[this.datasetName];
    },
    isLiveMode: function() {
      return this.hasDateRangePicker && this.dateRangeModel.getLiveModeStatus();
    },
  },
  watch: {
    routeParams: function() {
      this.routeUpdated(this.routeParams);
    }
  },
  components: {
    CsvActionsModal,
    EditModal,
    DashboardContainer,
    DataTable,
    DateSelectionPopup,
    DialBackComponent,
    FilterExpression,
    SavedSearchPopup,
  },
  methods: {
    dialBackCurrentRangeHandler(range) {
      this.dateRangeModel.setCurrentRange(range);
    },
    addFavorite() {
      if (this.filterModel.getActiveFilters()) {
        this.isSavedSearchAdding = true;
        this.openFavorites();
      }
    },
    getViewIds() {
        var self = this;
        return {
            appId: self.appId,
            viewId: self.viewId
        };
    },
    routeUpdated(routeParams) {
      let self = this;
      this.datasetName = this.routeParams.datasetName;
      this.nestedFieldSeparator = this.routeParams.nestedFieldSeparator;
      this.bottomDrawerDatasetName = BOTTOM_DRAWER_DATASET_MAP[this.datasetName];
      this.appId = this.routeParams.appId;
      this.hasDateRangePicker = this.routeParams.hasDateRangePicker;
      let drm = new DateRangeModel(self.appId);
      if (this.routeParams.liveModeStatus !== undefined) {
        drm.setLiveModeStatus(this.routeParams.liveModeStatus);
      }
      this.dateRangeModel = drm;
      this.hasDashboard = this.routeParams.hasDashboard;
      this.pageId = this.routeParams.pageId || 'dataset';
      this.title = this.routeParams.title || 'Dataset';
      this.viewId = 'dataset_' + this.datasetName.replace('.', '_');
      this.loadPreferences();
      this.filterModel = new FilterModel(true, this.appId + this.pageId);
      this.filterModel.activeFiltersUpdated.add(
        function () {
          self.updateHistory();
        }
      );
      this.watchOrgContext = this.routeParams.watchOrgContext;
    },
    deleteHistory(history) {
      this.history = this.history.filter((current) => current.created !== history.created);
      this.isSavedSearchVisible = true;
      PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId, { history: this.history });
    },
    deleteFavorite(favorite) {
      this.favorites = this.favorites.filter((current) => current.created !== favorite.created);
      this.isSavedSearchVisible = true;
      PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId, { favorites: this.favorites });
    },
    enableHistory(history) {
      if (history && history.filters) {
        this.filterModel.setActiveFilters(history.filters);
      }
    },
    enableFavorite(favorite) {
      if (favorite && favorite.filters) {
        this.filterModel.setActiveFilters(favorite.filters);
      }
    },
    favoriteRenamed(favorite) {
      favorite.isEditing = false;
      PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId, { favorites: this.favorites });
    },

    addNewFavorite(name) {
        this.isSavedSearchAdding = false;
        let favorites = this.favorites;
        if (!favorites) {
          return; // if favorites aren't fetched short circuit so we don't overwrite
        }
        favorites.push({
          name: name,
          created: Date.now(),
          filters: this.filterModel.getActiveFilters()
        });
        let favoriteLimit = DIAL_BACK_TO_FAVORITE_LIMITS[this.DIAL_BACK_HOURS] || 1;
        if (favorites.length > favoriteLimit) {
          favorites = favorites.slice(1);
        }
        this.favorites = favorites;
        PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId, { favorites: favorites });
    },
    editFinished() {
      this.showRowEditModal = false;
      this.reloadData();
    },
    loadParticipantsData(meetingId, startTime) {
      let self = this;
      self.bottomDrawerTableDataLoading = true;
      DataServiceSecure.fetchMeetingParticipants({ meetingId, startTime })
        .then(data => {
          if (!!data) {
            self.bottomDrawerTableData = data;
            self.bottomDrawerTableDataLoading = false;
          }
        });
    },

    editRow(row, reloadData) {
      this.showRowEditModal = true;
      this.editingRow = row.getData();
      this.reloadData = reloadData;
    },
    
    // row is a tabulator RowComponent object
    rowClickCallback(row, event, reloadData) {
      let self = this;
      let rowData = row.getData();
      let meetingId = rowData.meeting_id;
      let startTime = rowData.start_time;
      let meetingName = rowData.name;
      let systemType = rowData.type;
      let participantCount = rowData.participant_count;
      let prevSelectedRow = self.selectedRow;

      self.selectedRow = row;

      if (prevSelectedRow === self.selectedRow) {
        // close drawer if the already-open row was clicked
        self.drawerOpen = false;
        self.selectedRow.getElement().classList.remove('tabulator-selected');
        self.selectedRow = undefined;
        return;
      }

      if (participantCount) {
        meetingName = `${meetingName} (${participantCount} participants)`;
      }

      let old_defaults = [
        'device_type',
        'device_platform_device_version',
        'device_platform',
        'device_version',
        'public_ip_local_ip',
        'local_ip',
        'public_ip',
        'connection_type',
        'media_node',
        'camera_microphone_speaker',
        'camera',
        'microphone',
        'speaker',
        'user_feedback_score',
        'disconnect_details',
        'disconnect_reason',
      ];
      let hiddenColumns = {
        mcu: old_defaults,
        ctps: old_defaults,
        cms: [
          'device_type',
          'device_platform_device_version',
          'device_platform',
          'device_version',
          'public_ip_local_ip',
          'local_ip',
          'public_ip',
          'connection_type',
          'media_node',
          'camera_microphone_speaker',
          'camera',
          'microphone',
          'speaker',
          'bitrate',
          'user_feedback_score',
          'disconnect_details',
          'disconnect_reason',
        ],
        acano: [
          'device_type',
          'device_platform_device_version',
          'device_platform',
          'device_version',
          'public_ip_local_ip',
          'local_ip',
          'public_ip',
          'connection_type',
          'media_node',
          'camera_microphone_speaker',
          'camera',
          'microphone',
          'speaker',
          'bitrate',
          'user_feedback_score',
          'disconnect_details',
          'disconnect_reason',
        ],
        pexip: old_defaults,
        rmx: old_defaults,
        skype: old_defaults,
        vcs: old_defaults,
        vcx: old_defaults,
        dma: _.union(old_defaults, ['quality', 'bitrate', 'presentation']),
        cucm: _.union(old_defaults, ['quality', 'bitrate', 'presentation']),
        cucm_cdr: [
          'device_type',
          'device_platform_device_version',
          'device_platform',
          'device_version',
          'public_ip_local_ip',
          'local_ip',
          'public_ip',
          'connection_type',
          'media_node',
          'camera_microphone_speaker',
          'camera',
          'microphone',
          'speaker',
          'user_feedback_score',
          'quality', 'bitrate', 'presentation'
        ],
        bjn: [
          'participant_alias',
          'device_platform',
          'device_version',
          'local_ip',
          'public_ip',
          'connection_type',
          'camera',
          'microphone',
          'speaker',
          'user_feedback_score',
          'disconnect_details',
          'disconnect_reason',
        ],
        zoom: [
          'participant_alias',
          'device_type',
          'device_platform',
          'device_version',
          'public_ip_local_ip',
          'local_ip',
          'camera',
          'microphone',
          'speaker',
          'bitrate',
          'user_feedback_score',
        ],
        ciscowebex: [
          'participant_alias',
          'device_platform',
          'device_version',
          'local_ip',
          'public_ip',
          'camera',
          'microphone',
          'speaker',
          'bitrate',
          'user_feedback_score',
          'disconnect_details',
          'disconnect_reason',
        ],
        msteams: [
          'participant_alias',
          'device_platform',
          'device_version',
          'local_ip',
          'public_ip',
          'media_node',
          'camera',
          'microphone',
          'speaker',
          'bitrate',
          'presentation',
          'endpoint_id',
          'room_name',
          'building_name',
          'network_name'
        ],
        gmeet: [
          'participant_alias',
          'device_type',
          'device_platform_device_version',
          'device_platform',
          'device_version',
          'public_ip_local_ip',
          'local_ip',
          'connection_type',
          'media_node',
          'camera_microphone_speaker',
          'camera',
          'microphone',
          'speaker',
          'bitrate',
          'disconnect_details',
          'disconnect_reason',
          'endpoint_id',
        ],
        def: old_defaults,
      };

      if (self.datasetName === 'meetings' && meetingId && startTime) { // row expand functionality is only available for meetings dataset currently
        self.loadParticipantsData(meetingId, startTime); // todo support historical2 CallXml child callqualityxml
        if (prevSelectedRow) {
          prevSelectedRow.getElement().classList.remove('tabulator-selected');
          self.showDrawer = false;
          // delay re-adding class to allow for the transition animation
          setTimeout(() => {
            updateDrawer();
          }, 250); // This timeout should match the css transition speed
        } else {
          updateDrawer();
        }
        self.selectedRow.getElement().classList.add('tabulator-selected');
      }
      // edit functionality
      else if (self.datasetName === 'buildings' || self.datasetName === 'spaces' || self.datasetName === 'networks' || self.datasetName === 'sensors') { //|| self.datasetName === 'last-documents'
        this.showRowEditModal = true;
        this.editingRow = row.getData();
        this.reloadData = reloadData;
      }

      function updateDrawer() {
        if (self.bottomDrawerTableDataLoading) {
          self.bottomDrawerTableData = [];
          // todo show loading
        }
        self.bottomDrawerTitle = meetingName;
        self.drawerOpen = true;
      }

    },

    autoReloadCallback() {
      let self = this;
      let row = self.selectedRow;

      if (!row) {
        return;
      }

      let data = row.getData();
      let meetingId = data.meeting_id;
      let startTime = data.start_time;

      if (self.datasetName === 'meetings' && meetingId && startTime) {
        self.loadParticipantsData(meetingId, startTime);
      }
    },

    dataLoadedCallback(data, visibleData, tabulatorRows) {
      let self = this;
      let prevSelectedRow = self.selectedRow;
      if (prevSelectedRow && self.datasetName === 'meetings') {
        let meetingId = prevSelectedRow.getData().meeting_id;
        if (meetingId) {
          let selectedRow = tabulatorRows.find(row => {
            return row.getData().meeting_id === meetingId;
          });
          if (selectedRow) {
            selectedRow.getElement().classList.add('tabulator-selected');
            self.selectedRow = selectedRow;
          }
        }
      }
    },

    closeBottomDrawer() {
      let self = this;
      this.drawerOpen = false;
      this.drawerExpanded = false;
      if (self.selectedRow) {
        self.selectedRow.getElement().classList.remove('tabulator-selected');
        self.selectedRow = undefined;
      }
    },

    updateFilterGroups(filterGroups) {
        let filters = this.filterModel.getActiveFilters();
        filters.filterGroups = filterGroups;
        this.filterModel.setActiveFilters(filters);
        this.updateHistory();
    },
    updateOperator(operator) {
        this.filterModel.setOperator(operator);
        this.updateHistory();
    },

    // with more complex filters we no longer want to store string in url
    updateHistory() {
        let history = this.history;
        let href = window.location.href;

        // remove the existing filter parameter if there
        if (href.indexOf('?filter=') > -1) {
            href = href.replace(/\b\?filter=[^&$]*/, '');
            window.history.replaceState("", null, href);
        }

        let newFilter = this.filterModel.getActiveFilters();
        // sort circuit if prefs not fetched yet or no filters
        if (!history || newFilter.filterGroups.length === 0 || newFilter.filterGroups[0].filters.length === 0) {
            return;
        }
        let newHistory = {
            name: this.filterModel.getActiveFiltersDisplay(),
            created: Date.now(),
            filters: this.filterModel.getActiveFilters()
        };
        let added = false;
        history.forEach(function(historyItem) {
            if(historyItem.name === newHistory.name) {
                added = true;
            }
        });
        if (!added) {
            history.push(newHistory);
            let historyLimit = DIAL_BACK_TO_HISTORY_LIMITS[this.DIAL_BACK_HOURS] || 10;
            if (history.length > historyLimit) {
                history = history.slice(1);
            }
            this.history = history;
            PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId, { history: history });
        }
    },

    async loadPreferences() {
        let self = this;
        let viewIds = self.getViewIds();
        if (!viewIds.appId || !viewIds.viewId) {
            return;
        }

        let datasetMetadata = await MetadataService.fetchDatasetMetadataWithoutCategories(self.datasetName);
        self.datasetMetadata = datasetMetadata;

        let availableFilters = _.get(self.datasetMetadata, 'columns');
        if (!_.isNil(availableFilters)) {
          if (self.filterColumns.length > 0) {
            self.filterColumns.length = 0;
          }
          let filteredColumns = FilterExpressionService.getColumnsFromFilters(availableFilters.filter(column => column.datasetId || (!column.invisible && !column.suppressFiltering))).filter(column => column.filterType !== 'none');
          self.filterColumns.push(...filteredColumns);
        }

        return PreferenceService
        .getDatasetViewPreferences(viewIds.appId, viewIds.viewId, datasetMetadata)
        .then(function (preferences) {
            self.createFiltersFromQueryString(); // dataset.updateGridServiceMetadata needs to have finished before this call
            if (!_.isEmpty(preferences)) {
                self.history = preferences.history || [];
                self.favorites = preferences.favorites || [];
            } else {
              // init history and favorites anyway so we can save
              self.history = [];
              self.favorites = [];
            }
        });
    },

    // creates a list of active filters from the query string created in `updateQueryStringFromActiveFilters`
    createFiltersFromQueryString() {
        var self = this;
        var filterParam = _.get(self, 'routeParams["?query"].filter', '');
        if (filterParam === "") {
            return;
        }
        var createdFilters = self.filterModel.getFiltersFromString(filterParam);
        self.filterModel.setActiveFilters(createdFilters);
    },

    openFavorites(){
        this.isSavedSearchVisible = true;
        this.activeSavedSearchTab = FAVORITES_TAB;
    },

    openHistory(){
        this.isSavedSearchVisible = true;
        this.activeSavedSearchTab = HISTORY_TAB;
    },

    popupClose() {
        this.isSavedSearchAdding = false;
        this.isSavedSearchVisible = false;
    },

    showCaptureModal(event) {
        const CAPTURE_MODAL_ID = 'AssuranceCaptureModal';
        var self = this;

        var modalEl = $('#' + CAPTURE_MODAL_ID);

        var captureModalVM = ko.dataFor(_.first(modalEl));
        captureModalVM.initialize(self.captureType, self.pageId, self.panelId);

        modalEl.one('shown.bs.modal', function (e) {
            captureModalVM.execute();
        });

        modalEl.modal('show');
    },
  },
  mounted: function() {
    this.loadPreferences();
    window.adrDocumentDownload = function(s3filename, id) { // todo this sucks here
      DataProvider.getInstance().fetchAssuranceAdrServiceBaseURL().then(baseUrl => {
        self.downloadProgress = 0;
        let filename = s3filename.substring(s3filename.lastIndexOf('/') + 1);
        WebResourceSecure.getBlob(baseUrl + '/download/' + id,
            undefined, 'GET', filename, function (evt) {
              // console.log(evt.loaded, evt.total);
              // todo show progress in UI?
              if (evt.lengthComputable) {
                self.downloadProgress = Math.floor(evt.loaded * 100 / evt.total);
              }
            }).then(function () {
          self.downloadDisabled = true;
        })
        return true;

      });
    }
  },
  beforeDestroy: function() {
    window.adrDocumentDownload = undefined;
  }
}
</script>
<style lang="scss">

#adminApp .dataset-view { // ugh special case for VAP with status footer bar
  .dataset-view-content {
    &.drawer-open {
      height: calc(100vh - 76px - 50px - 359px); // subtract total height of header, footer and bottom drawer

      .data-table-wrapper .vy-table-wrapper .vy-table.tabulator,
      .data-table-wrapper .vy-table-wrapper .vy-table .tabulator {
        height: var(--vue-datatable-height, calc(100vh - 40px - 600px));
      }

    }

    .data-table-wrapper .vy-table-wrapper .vy-table.tabulator,
    .data-table-wrapper .vy-table-wrapper .vy-table .tabulator {
      height: var(--vue-datatable-height, calc(100vh - 40px - 255px));
    }
  }
}

.dataset-view {
  .dataset-header .buttonContainer {
    display:flex;
    margin-bottom: 17px;

    .assurance-capture-button.vyopta-btn-light-blue {
      border-radius: 0px;
      background-color: var(--secondary-btn-bg-color);
      border: 1px solid var(--secondary-btn-border-color);
      border-right: 0px;
      height: 40px;
      width: 50px;
      font-size: 18px;
      color: var(--secondary-btn-color);
      &:hover {
        background-color: var(--active-background);
      }
    }
    .assurance-header-button {
      background-color: var(--secondary-btn-bg-color);
      border: 1px solid var(--secondary-btn-border-color);
      border-right: 0px;
      height: 40px;
      width: 50px;
      font-size: 18px;
      border-radius: 0px;
      padding: 8px 10px;
      color: var(--secondary-btn-color);
      &:hover {
        background-color: var(--active-background);
      }
      &:active, &:focus {
        outline: inherit;
      }
      &.left-most {
        border-top-left-radius: var(--border-radius);
        border-bottom-left-radius: var(--border-radius);
      }
      &.right-most {
        border-top-right-radius: var(--border-radius);
        border-bottom-right-radius: var(--border-radius);
        border: 1px solid var(--secondary-btn-border-color);
      }
    }
    .left-most.btn.page-capture {
      border-top-left-radius: var(--border-radius);
      border-bottom-left-radius: var(--border-radius);
    }
    .right-most .btn.page-capture {
      border-top-right-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
      border: 1px solid var(--secondary-btn-border-color);
    }
  }

  .filter-expression-wrapper {
    padding: 20px;
    margin-bottom: 20px;
    background-color: var(--panel-background);
    border-radius: var(--border-radius);
  }

  .dataset-view-content {
    height: auto;
    border-radius: var(--border-radius);
    &.drawer-open {
      height: calc(100vh - 76px - 359px); // subtract total height of header and bottom drawer
      overflow: auto; 

      .data-table-wrapper .vy-table-wrapper .vy-table.tabulator,
      .data-table-wrapper .vy-table-wrapper .vy-table .tabulator {
        height: var(--vue-datatable-height, calc(100vh - 550px)); 
        margin-bottom: 0;
      }

    }

    .data-table-wrapper .vy-table-wrapper .vy-table.tabulator,
    .data-table-wrapper .vy-table-wrapper .vy-table .tabulator {
      height: var(--vue-datatable-height, calc(100vh - 215px));
      margin-bottom: 0;
    }

    .inner-content {
      display: block;
      position: relative;
      height: 100%;
      width: 100%;
    }

    .inner-content > .overlay {
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      z-index: 10;
    }

    .inner-content .overlay > .loadingSpinner {
      position: fixed;
      top: 45%;
      left: 50%;
      border-radius: 5px;
      text-align: center;
      z-index: 11;
      font-size: 60px;
    }
  }

  .dataset-view-bottom-drawer {
    position: fixed;
    z-index: 1000;
    background-color: var(--panel-background);
    bottom: -500px;
    height: 325px;
    width: calc(100% - var(--side-bar-width) - 65px); // since this is a fixed element we need to subtract the width of the sidebar and padding
    transition: all 0.25s ease-in-out;
    border-radius: 24px 24px 0 0;

    &.show-drawer {
      bottom: 0px;
      transition: all 0.125s ease-in-out;
    }
    &.drawer-expanded {
      height: calc(100vh - 79px);
      top: 77px;
      .tabulator {
        height: calc(100vh - 150px) !important;
      }
    }

    .bottom-drawer-header {
      padding-bottom: 10px;
      padding-top: 10px;

      .bottom-drawer-border {
      }

      .bottom-drawer-title {
        font-size: 16px;
        font-weight: bold;
        color: var(--text-color);
        margin-left: 21px;
      }

      .bottom-drawer-icons {
        float: right;
        font-size: 18px;
        color: #6D787E;
        cursor: pointer;
        margin-right: 20px;

        i {
          margin-left: 5px;
          font-size: 24px;
          color: #6D787E;

          &:hover {
            color: #253035;
          }
        }
      }
    }

    .bottom-drawer-table-container {
      .data-table-wrapper { 
        padding: 10px 20px;

        .vy-table-wrapper {
          height: calc(100% - 60px);
          .vy-table {
            .edit-table-spacer2 {
              //background-color: #6D787E;
            }
          }
          .modal-opener {
            //background-color: #6D787E;
            
            &:hover {
              //background-color: #404E55;
            }
          }
        }

        .tabulator {
          height: 250px; // setting a static height allows tabulator's virtual dom to optimize dom operations

          .tabulator-header {
            border-bottom: 1px solid var(--table-header-border-color);
            .tabulator-headers {
              //background-color: #6D787E;
              .tabulator-col {
                border-right: 1px solid var(--table-header-border-color);
                //background-color: #6D787E;
                &.tabulator-sortable:hover {
                  //background-color: #404E55;
                }
                &.tabulator-sortable:hover[aria-sort=none] .tabulator-col-content .tabulator-arrow {
                  //border-bottom-color: #9a9a9a;
                }
              }
            }
          }
        }
      }
    }
  }

  .modal-container {
    max-height: 90vh;
    overflow: hidden;

    .modal-subheader {
      font-weight: bold;
      font-size: 16px;;
    }

    .modal-header div {
      position: relative;
      h1 {
        margin: 0;
        font-size: 18px;
        font-weight: bold;
        line-height: 35px;
      }
      .text-xs-right {
        position: absolute;
        right: -5px;
        top: -10px;

        .close-icon {
          cursor: pointer;
        }
      }
    }
  }
  
  .data-table-wrapper {
    .tabulator {
      .tabulator-table .tabulator-row .tabulator-cell .peripherals-icon {
        margin-right: 8px;
        min-width: 15px;
      }
    }
  }
}

.status-icon {
  margin-left: 5px;
  margin-right: 5px;
}

.external-call-details-link-icon {
    padding-left: 10px;
    display: inline;
}

.call-details-link-icon {
    padding-left: 5px;
}

.star-feedback {
  color: #6D787E;
}


.call-list-endtime-inprogress-container {
    display: flex;
    justify-content: left;
    align-items: center;
  &.small-badge {
    flex-direction: column;
  }
  > span:not(.small-badge) {
    width: 100%;
  }
  > i {
    margin-right: 5px;
  }
}

.call-list-endtime-inprogress {
    color: var(--disabled-text);
    border-radius: 3px;
    font-family: var(--font-family);
    font-size: 12px;
    line-height: 14px;
    letter-spacing: unset;
    text-transform: uppercase;
    width: 100%;
    max-width: 135px;
    margin: 0;

    &.small-badge {
      margin-bottom: 2px;
      margin-top: 2px;
    }
}


@media (max-height: 900px) {
  .dataset-view-content {
    height: auto;

    &.drawer-open {
      height: calc(100vh - 76px - 350px); // subtract total height of header and bottom drawer
    }
  }

  .dataset-view-bottom-drawer {
    bottom: -350px;
    height: 350px;

    .bottom-drawer-table-container .data-table-wrapper .tabulator {
      height: 250px;
    }
  }
}

.sidebar-collapse .dataset-view-bottom-drawer {
  width: calc(100% - 75px); // since this is a fixed element we need to subtract the width of the sidebar
}
</style>